import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../../direct-availability.scss';
import { useDirectAvailability, AgendaView, EditorTab } from '../../hooks/useDirectAvailability';
import { TimePicker } from '../Pickers/TimePicker';
import { DatePicker } from '../Pickers/DatePicker';
import { ReplicateModal } from '../ReplicateModal/ReplicateModal';

export const EditorColumn: React.FC = () => {
  const { t } = useTranslation();
  const { state, actions, saveData, replicateData, isSaving, hasChanges, isReplicateOpen, setIsReplicateOpen } = useDirectAvailability();
  const [isPauseMenuOpen, setIsPauseMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const dayNames = [
    t('directAvailability.days.su', 'Domingo'),
    t('directAvailability.days.mo', 'Lunes'),
    t('directAvailability.days.tu', 'Martes'),
    t('directAvailability.days.we', 'Miércoles'),
    t('directAvailability.days.th', 'Jueves'),
    t('directAvailability.days.fr', 'Viernes'),
    t('directAvailability.days.sa', 'Sábado')
  ];

  const dayNamesShort = [
    t('directAvailability.daysShort.mo', 'LU'),
    t('directAvailability.daysShort.tu', 'MA'),
    t('directAvailability.daysShort.we', 'MI'),
    t('directAvailability.daysShort.th', 'JU'),
    t('directAvailability.daysShort.fr', 'VI'),
    t('directAvailability.daysShort.sa', 'SÁ'),
    t('directAvailability.daysShort.su', 'DO'),
  ];

  const monthNames = [
    t('directAvailability.months.jan', 'enero'),
    t('directAvailability.months.feb', 'febrero'),
    t('directAvailability.months.mar', 'marzo'),
    t('directAvailability.months.apr', 'abril'),
    t('directAvailability.months.may', 'mayo'),
    t('directAvailability.months.jun', 'junio'),
    t('directAvailability.months.jul', 'julio'),
    t('directAvailability.months.aug', 'agosto'),
    t('directAvailability.months.sep', 'septiembre'),
    t('directAvailability.months.oct', 'octubre'),
    t('directAvailability.months.nov', 'noviembre'),
    t('directAvailability.months.dec', 'diciembre')
  ];

  // Fake states for Pickers
  const selectedBlock = state.availabilities.find(a => a.id === state.selectedAvailabilityId);
  const activeBlock = state.draft || selectedBlock;

  const fromTime = activeBlock ? activeBlock.start : '09:00';
  const toTime = activeBlock ? activeBlock.end : '13:00';
  const selectedDays = state.selectedApplyDays;

  const [editCutFrom, setEditCutFrom] = useState('10:00');
  const [editCutTo, setEditCutTo] = useState('11:00');

  const [timeError, setTimeError] = useState<string | null>(null);

  const toMins = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

  const setFromTime = (val: string) => {
    if (state.editMode === 'edit-availability') {
      setEditCutFrom(val);
      return;
    }
    if (state.draft) {
      setTimeError(null);
      actions.setDraft({ ...state.draft, start: val });
    } else if (state.selectedAvailabilityId && selectedBlock) {
      const newEnd = selectedBlock.end;
      if (toMins(val) >= toMins(newEnd)) {
        setTimeError(t('directAvailability.availability.errorFromAfterTo', 'El horario de inicio debe ser menor al horario de fin.'));
        return;
      }
      if (toMins(newEnd) - toMins(val) < 15) {
        setTimeError(t('directAvailability.availability.errorMinDuration', 'La franja debe tener al menos 15 minutos.'));
        return;
      }
      setTimeError(null);
      actions.updateAvailability(state.selectedAvailabilityId, val, newEnd);
    }
  };

  const setToTime = (val: string) => {
    if (state.editMode === 'edit-availability') {
      setEditCutTo(val);
      return;
    }
    if (state.draft) {
      setTimeError(null);
      actions.setDraft({ ...state.draft, end: val });
    } else if (state.selectedAvailabilityId && selectedBlock) {
      const newStart = selectedBlock.start;
      if (toMins(val) <= toMins(newStart)) {
        setTimeError(t('directAvailability.availability.errorToBeforeFrom', 'El horario de fin debe ser mayor al horario de inicio.'));
        return;
      }
      if (toMins(val) - toMins(newStart) < 15) {
        setTimeError(t('directAvailability.availability.errorMinDuration', 'La franja debe tener al menos 15 minutos.'));
        return;
      }
      setTimeError(null);
      actions.updateAvailability(state.selectedAvailabilityId, newStart, val);
    }
  };

  // Sync edit mode cut times when entering the mode
  useEffect(() => {
    if (state.editMode === 'edit-availability' && selectedBlock) {
      setEditCutFrom(selectedBlock.start);
      setEditCutTo(selectedBlock.end);
    }
  }, [state.editMode, selectedBlock]);

  // Clear time error when active block changes
  useEffect(() => {
    setTimeError(null);
  }, [state.selectedAvailabilityId, state.draft?.weekday]);

  const [adjustDate, setAdjustDate] = useState('2026-10-06');
  const [adjustFromTime, setAdjustFromTime] = useState('09:00');
  const [adjustToTime, setAdjustToTime] = useState('13:00');

  const toggleDay = (dayIndex: number) => {
    const next = new Set(selectedDays);
    if (next.has(dayIndex)) {
      next.delete(dayIndex);
    } else {
      next.add(dayIndex);
    }
    // @ts-ignore
    if (actions.setApplyDays) actions.setApplyDays(next);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsPauseMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="editor-column" id="editorColumn">
      <aside className="editor-card" id="editorCard">
        {state.view !== 'week' && (
          <div className="readonly-notice show" id="readonlyNotice">
            <strong>{t('directAvailability.readOnlyNotice.title', 'Vista de consulta')}</strong>
            <p>{t('directAvailability.readOnlyNotice.text', 'Día y Mes sirven para revisar la agenda. Para modificar esta fecha, abrí su Semana.')}</p>
            <button 
              className="btn secondary" 
              style={{ width: '100%' }} 
              type="button"
              onClick={() => actions.setView('week')}
            >
              {t('directAvailability.readOnlyNotice.editBtn', 'Editar esta semana')}
            </button>
          </div>
        )}

        {state.view === 'week' && (
          <>
            <div className="tabs editor-tabs" role="tablist">
              <button 
                aria-selected={state.editorTab === 'availability'} 
                className={`tab ${state.editorTab === 'availability' ? 'active' : ''}`} 
                data-editor-tab="availabilityEditor"
                onClick={() => actions.setEditorTab('availability')}
              >
                {t('directAvailability.tabs.availability')}
              </button>
              <button 
                aria-selected={state.editorTab === 'date-adjustments'} 
                className={`tab ${state.editorTab === 'date-adjustments' ? 'active' : ''}`} 
                data-editor-tab="dateAdjustmentsEditor"
                onClick={() => actions.setEditorTab('date-adjustments')}
              >
                {t('directAvailability.tabs.adjustments')}
              </button>
            </div>

            {state.editorTab === 'availability' && (() => {
              const isAdjust = state.editMode === 'edit-availability';
              const fromVal = isAdjust ? editCutFrom : fromTime;
              const toVal = isAdjust ? editCutTo : toTime;
              const isTimeValid = fromVal < toVal;

              const originD = state.draft ? state.draft.weekday : (selectedBlock ? selectedBlock.weekday : null);

              let title = t('directAvailability.availability.title', 'Disponibilidad');
              let lead = t('directAvailability.availability.subtitle', 'Prepará una franja en la grilla o seleccioná una existente para editarla.');
              let noteStr = t('directAvailability.availability.selectNote', 'Seleccioná o prepará una franja en el calendario.');
              let actionLabel = t('directAvailability.availability.createBtn', 'Crear franja');
              
              if (isAdjust && selectedBlock) {
                title = t('directAvailability.availability.editTitle', 'Editar disponibilidad');
                lead = t('directAvailability.availability.editLead', 'Elegí el tramo que dejará de estar disponible dentro de esta franja.');
                noteStr = t('directAvailability.availability.editNote', 'Si elegís un extremo, se recorta; si elegís una parte central, se divide. Si elegís todo, la franja desaparece.');
                actionLabel = t('directAvailability.availability.saveAdjustBtn', 'Guardar ajuste');
              } else if (selectedBlock) {
                title = t('directAvailability.availability.editTitle', 'Editar disponibilidad');
                lead = t('directAvailability.availability.editLeadActive', 'Modificá el horario de la franja seleccionada.');
                noteStr = t('directAvailability.availability.editNoteActive', 'Editá Desde/Hasta y actualizá la franja.');
                actionLabel = t('directAvailability.availability.updateBtn', 'Actualizar franja');
              } else if (state.draft) {
                title = t('directAvailability.availability.newTitle', 'Nueva franja');
                lead = t('directAvailability.availability.newLead', 'Ajustá Desde/Hasta y confirmá la nueva disponibilidad.');
                noteStr = t('directAvailability.availability.newNote', 'Franja preparada, todavía sin guardar.');
                actionLabel = t('directAvailability.availability.createBtn', 'Crear franja');
              }

              return (
              <div className="editor-panel active" id="availabilityEditor">
                <h2 id="editorTitle">{title}</h2>
                <p className="lead" id="editorLead">{lead}</p>
                <div className={`selection-note selected-range-info ${isAdjust ? 'availability-edit-note' : ''}`} id="selectionNote">
                  <div className="selected-range-copy">
                    {activeBlock && <strong>{dayNamesShort[(activeBlock.weekday - 1 + 7) % 7]} · {activeBlock.start}–{activeBlock.end}</strong>}
                    {activeBlock && <br />}
                    {noteStr}
                    {!isTimeValid && activeBlock && (
                      <span style={{ display: 'block', color: '#C95C5C', marginTop: '4px' }}>
                        {t('directAvailability.availability.invalidTime', 'El horario de inicio debe ser menor al de fin.')}
                      </span>
                    )}
                    {timeError && (
                      <span style={{ display: 'block', color: '#C95C5C', marginTop: '4px', fontWeight: 600 }}>
                        ⚠ {timeError}
                      </span>
                    )}
                  </div>
                  {selectedBlock && (
                    <button
                      id="deleteAvailabilityBtn"
                      className="delete-slot-icon"
                      type="button"
                      aria-label={t('directAvailability.availability.removeBtn', 'Eliminar franja')}
                      title={t('directAvailability.availability.removeBtn', 'Eliminar franja')}
                      onClick={() => {
                        const dayName = dayNames[selectedBlock.weekday % 7];
                        actions.openDeleteHabitualModal(selectedBlock.id, `${dayName} · ${selectedBlock.start}–${selectedBlock.end}`);
                      }}
                    >
                      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18M8 6V4h8v2m3 0-1 14H6L5 6m5 4v6m4-6v6" />
                      </svg>
                    </button>
                  )}
                </div>

                
                <div className="pair">
                  <div className="field">
                    <label>{t('directAvailability.availability.from')}</label>
                    <TimePicker value={fromVal} onChange={setFromTime} disabled={!activeBlock} />
                  </div>
                  <div className="field">
                    <label>{t('directAvailability.availability.to')}</label>
                    <TimePicker value={toVal} onChange={setToTime} disabled={!activeBlock} />
                  </div>
                </div>

                <div className="field apply-days-field">
                  <label>{t('directAvailability.availability.applyTo')}</label>
                  <div className="day-chips-inline" id="dayMulti">
                    {dayNamesShort.map((dayStr, idx) => {
                      const dayIdx = idx + 1;
                      const isChecked = selectedDays.has(dayIdx);
                      const isOrigin = state.draft?.weekday === dayIdx;
                      return (
                        <button 
                          key={dayStr} 
                          className={`day-chip-inline ${isChecked ? 'checked' : ''} ${isOrigin ? 'origin' : ''}`} 
                          type="button"
                          data-day={dayIdx}
                          aria-pressed={isChecked}
                          onClick={() => toggleDay(dayIdx)}
                          disabled={isOrigin}
                        >
                          {dayStr}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="availability-actions">
                  <button 
                    className="btn secondary" 
                    type="button" 
                    id="createBtn"
                    disabled={!activeBlock || !isTimeValid}
                    onClick={() => {
                      if (isAdjust && selectedBlock) {
                        actions.editAvailabilityCut(selectedBlock.id, editCutFrom, editCutTo);
                      } else if (state.draft) {
                        const days = Array.from(selectedDays);
                        if (days.length === 0) days.push(state.draft.weekday);
                        days.forEach(d => {
                          actions.createAvailability({ weekday: d, start: state.draft!.start, end: state.draft!.end });
                        });
                      } else if (state.selectedAvailabilityId) {
                        actions.selectAvailability(null);
                      }
                    }}
                  >
                    {actionLabel}
                  </button>
                  <button 
                    className="btn secondary" 
                    type="button" 
                    id="applyBtn" 
                    disabled={!activeBlock || originD === null || !isTimeValid || !Array.from(selectedDays).some(d => d !== originD)}
                    onClick={() => {
                      if (!activeBlock || originD === null) return;
                      const targetDays = Array.from(selectedDays);
                      if (isAdjust && selectedBlock) {
                        actions.applyToDays(originD, targetDays, editCutFrom, editCutTo, undefined, true);
                        actions.editAvailabilityCut(selectedBlock.id, editCutFrom, editCutTo);
                      } else if (state.draft) {
                        actions.applyToDays(originD, targetDays, state.draft.start, state.draft.end);
                        actions.createAvailability({ weekday: originD, start: state.draft.start, end: state.draft.end });
                      } else if (selectedBlock) {
                        actions.applyToDays(originD, targetDays, selectedBlock.start, selectedBlock.end, { start: selectedBlock.start, end: selectedBlock.end });
                        actions.selectAvailability(null);
                      }
                    }}
                  >
                    {t('directAvailability.availability.applyToBtn')}
                  </button>
                </div>
                
                <button 
                  className="edit-availability-direct" 
                  id="editAvailabilityBtn" 
                  type="button" 
                  hidden={!selectedBlock || isAdjust}
                  onClick={() => actions.setEditMode('edit-availability')}
                >
                  {t('directAvailability.availability.editBtn')}
                </button>
                {isAdjust && (
                  <button 
                    className="edit-availability-direct" 
                    type="button" 
                    onClick={() => actions.setEditMode('standard')}
                  >
                    {t('directAvailability.availability.cancelEditBtn')}
                  </button>
                )}
                
                <div className="divider"></div>
                
                <div className="field pause-inline-field">
                  <label>{t('directAvailability.availability.pause')}</label>
                  <div className="pause-inline-row">
                    <div className={`system-menu pause-compact-menu ${isPauseMenuOpen ? 'open' : ''}`} data-value={state.pauseMinutes} id="pauseMenu" ref={menuRef}>
                      <button 
                        aria-expanded={isPauseMenuOpen} 
                        aria-haspopup="listbox" 
                        className="system-menu-btn" 
                        type="button"
                        onClick={() => setIsPauseMenuOpen(!isPauseMenuOpen)}
                      >
                        <span id="pauseLabel">{state.pauseMinutes === 0 ? t('directAvailability.availability.noPause') : `${state.pauseMinutes} min`}</span><span>⌄</span>
                      </button>
                      <div className="system-menu-popover" role="listbox">
                        {[0, 5, 10, 15, 20, 30].map(val => (
                          <button 
                            key={val}
                            className={`system-menu-option ${state.pauseMinutes === val ? 'selected' : ''}`} 
                            data-value={val} 
                            type="button"
                            onClick={() => {
                              actions.setPauseMinutes(val as any);
                              setIsPauseMenuOpen(false);
                            }}
                          >
                            {val === 0 ? t('directAvailability.availability.noPause') : `${val} min`}
                          </button>
                        ))}
                      </div>
                    </div>
                    <p className="pause-inline-help">{t('directAvailability.availability.pauseNote')}</p>
                  </div>
                </div>
                <button className="btn secondary replicate-btn" style={{ width: '100%' }} onClick={() => setIsReplicateOpen(true)}>{t('directAvailability.availability.replicateBtn')}</button>
              </div>
              );
            })()}
            
            {state.editorTab === 'date-adjustments' && (() => {
              const adjDate = state.adjustmentDate;
              const hasOverride = state.dateOverrides.has(adjDate);
              const dateObj = new Date(adjDate + 'T00:00:00');
              const dayOfWeek = dateObj.getDay() === 0 ? 7 : dateObj.getDay();
              const habitualIntervals = state.availabilities
                .filter(a => a.weekday === dayOfWeek)
                .map(a => ({ start: a.start, end: a.end }));
              habitualIntervals.sort((a, b) => a.start.localeCompare(b.start));

              const intervals = hasOverride ? state.dateOverrides.get(adjDate)! : habitualIntervals;
              const selIdx = state.selectedDateIntervalIndex;
              const selInterval = selIdx !== null && intervals[selIdx] ? intervals[selIdx] : null;
              const dateDraftActive = state.dateDraft && state.dateDraft.date === adjDate && !state.dateDraft.originalBlockId;

              // Determine from/to for pickers
              const adjFromTime = selInterval ? selInterval.start : (dateDraftActive ? state.dateDraft!.start : (intervals[0]?.start || '09:00'));
              const adjToTime   = selInterval ? selInterval.end   : (dateDraftActive ? state.dateDraft!.end   : (intervals[0]?.end || '13:00'));
              const pickersDisabled = !selInterval && !dateDraftActive;

              // Status label
              const statusLabel = t('directAvailability.adjustStatusFormat', {
                dayName: dayNames[dateObj.getDay()],
                dayNum: dateObj.getDate(),
                monthName: monthNames[dateObj.getMonth()],
                defaultValue: `${dayNames[dateObj.getDay()]} ${dateObj.getDate()} de ${monthNames[dateObj.getMonth()]}`
              });
              const statusSub   = hasOverride ? t('directAvailability.adjustments.overrideStatus', 'Ajuste para esta fecha') : t('directAvailability.adjustments.habitualStatus', 'Disponibilidad habitual');
              const statusInfo  = selInterval ? ` · ${selInterval.start}–${selInterval.end}` : '';

              // Action button label
              let actionLabel = t('directAvailability.adjustments.selectSlotBtn', 'Seleccionar una franja');
              let actionDisabled = true;
              if (selInterval) { 
                actionLabel = t('directAvailability.adjustments.updateSlotBtn', 'Actualizar franja'); 
                actionDisabled = false; 
              }
              else if (dateDraftActive) { 
                actionLabel = t('directAvailability.adjustments.createSlotBtn', 'Crear franja'); 
                actionDisabled = false; 
              }

              const handleAdjFromTime = (val: string) => {
                if (selIdx !== null) actions.updateDateInterval(adjDate, selIdx, val, adjToTime);
                else if (dateDraftActive) actions.setDateDraft({ date: adjDate, start: val, end: adjToTime });
              };
              const handleAdjToTime = (val: string) => {
                if (selIdx !== null) actions.updateDateInterval(adjDate, selIdx, adjFromTime, val);
                else if (dateDraftActive) actions.setDateDraft({ date: adjDate, start: adjFromTime, end: val });
              };
              const handleAction = () => {
                if (selIdx !== null) {
                  actions.updateDateInterval(adjDate, selIdx, adjFromTime, adjToTime);
                } else if (dateDraftActive) {
                  actions.createDateInterval(adjDate, state.dateDraft!.start, state.dateDraft!.end);
                }
              };

              return (
                <div className="editor-panel active" id="dateAdjustmentsEditor">
                  <h2 id="dateEditorTitle">{t('directAvailability.tabs.adjustments')}</h2>
                  <p className="lead" id="dateEditorLead">{t('directAvailability.adjustments.subtitle', 'Modificá la disponibilidad de un día concreto sin cambiar tu patrón habitual.')}</p>

                  <div className="field">
                    <label>{t('directAvailability.adjustments.date')}</label>
                    <DatePicker value={adjDate} onChange={actions.setAdjustmentDate} />
                  </div>

                  <div className="selection-note date-adjust-status selected-range-info">
                    <div className="selected-range-copy" id="dateAdjustmentStatus">
                      <strong>{statusLabel}{statusInfo}</strong><br />{statusSub}
                    </div>
                    {selInterval && selIdx !== null && (
                      <button
                        id="dateDeleteAvailabilityBtn"
                        className="delete-slot-icon"
                        type="button"
                        aria-label={t('directAvailability.adjustments.removeSlotBtn', 'Eliminar franja')}
                        title={t('directAvailability.adjustments.removeSlotBtn', 'Eliminar franja')}
                        onClick={() => {
                          actions.openDeleteOverrideModal(adjDate, selIdx, `${statusLabel} · ${selInterval.start}–${selInterval.end}`);
                        }}
                      >
                        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18M8 6V4h8v2m3 0-1 14H6L5 6m5 4v6m4-6v6" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* List of override intervals for this date */}
                  {intervals.length > 0 && (
                    <div className="field">
                      <label>{t('directAvailability.adjustments.daySlots', 'FRANJAS DEL DÍA')}</label>
                      <div style={{ display: 'grid', gap: '5px' }}>
                        {intervals.map((iv, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className={`btn secondary ${selIdx === idx ? 'is-success' : ''}`}
                            style={{ fontSize: '11px', height: '34px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 10px' }}
                            onClick={() => actions.selectDateInterval(selIdx === idx ? null : idx)}
                          >
                            <span>{iv.start}–{iv.end}</span>
                            <span
                              role="button"
                              style={{ color: '#B34A4A', fontWeight: 900, marginLeft: 8, lineHeight: 1, cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                actions.openDeleteOverrideModal(adjDate, idx, `${statusLabel} · ${iv.start}–${iv.end}`);
                              }}
                              title={t('directAvailability.adjustments.remove', 'Eliminar')}
                            >✕</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}



                  <div className="pair">
                    <div className="field">
                      <label>{t('directAvailability.adjustments.from')}</label>
                      <TimePicker value={adjFromTime} onChange={handleAdjFromTime} disabled={pickersDisabled} />
                    </div>
                    <div className="field">
                      <label>{t('directAvailability.adjustments.to')}</label>
                      <TimePicker value={adjToTime} onChange={handleAdjToTime} disabled={pickersDisabled} />
                    </div>
                  </div>

                  <div className="date-adjust-actions">
                    <button
                      className="btn secondary"
                      style={{ width: '100%' }}
                      disabled={actionDisabled}
                      onClick={handleAction}
                    >
                      {actionLabel}
                    </button>
                  </div>

                  {hasOverride && (
                    <button
                      className="btn secondary"
                      id="restoreHabitual"
                      type="button"
                      style={{ width: '100%', marginTop: '8px', color: '#B34A4A', borderColor: 'rgba(179,74,74,.32)' }}
                      onClick={() => actions.restoreDateHabitual(adjDate)}
                    >
                      {t('directAvailability.adjustments.restoreHabitualBtn')}
                    </button>
                  )}
                </div>
              );
            })()}

          </>
        )}

      </aside>
      


      <ReplicateModal
        open={isReplicateOpen}
        onClose={() => setIsReplicateOpen(false)}
        onConfirm={(mode) => replicateData(mode)}
      />
    </div>
  );
};
